import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import { isEqual } from 'lodash/lang';

import PatientTravelApi from '../../../../../../../api/patient/PatientTravelApi';
import NotificationManager from '../../../../../../../common/notifications/NotificationManager';
import { NSPP_TRAVEL } from '../../../../../../../services/i18n/namespaces';

import { validationSchema } from './validationSchema';

export default function HotelPreferences({ initialData, setTravelPreferences }) {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({
    preferredHotelChain: '',
    specialAccommodation: '',
    ...initialData
  });

  const { preferredHotelChain, specialAccommodation } = state;

  const { saveAllowed, discardAllowed } = useMemo(
    function() {
      let saveAllowed = false;
      let discardAllowed = false;
      if (!saving && !isEqual(initialData, state)) {
        saveAllowed = true;
        discardAllowed = true;
      }
      return {
        saveAllowed,
        discardAllowed
      };
    },
    [initialData, state, saving]
  );

  useEffect(
    function() {
      setTravelPreferences(function(state) {
        return { ...state, hotelPreferencesEdited: saveAllowed };
      });
    },
    [saveAllowed, setTravelPreferences]
  );

  return (
    <Box sx={{ flexGrow: 1, my: 3 }} component="form">
      <Grid container spacing={2} alignItems="center">
        <Grid size={6}>
          <Typography variant="subtitle2">{t(`${NSPP_TRAVEL}:preferences.hotelPreferences`)}</Typography>
        </Grid>
        <Grid size={6}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: 'flex-end'
            }}
          >
            <IconButton aria-label="save" disabled={!saveAllowed} color="primary" onClick={onSave} title={t('save')}>
              <SaveIcon />
            </IconButton>
            <IconButton
              aria-label="discard"
              disabled={!discardAllowed}
              color="primary"
              onClick={onDiscard}
              title={t('discardChanges')}
            >
              <CloseIcon data-testid="CloseIcon" />
            </IconButton>
          </Stack>
        </Grid>
        <Grid size={5}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={6}>
                <TextField
                  name="preferredHotelChain"
                  value={preferredHotelChain}
                  onChange={handleChange}
                  fullWidth
                  label={t(`${NSPP_TRAVEL}:preferences.preferredHotelChain`)}
                  variant="standard"
                  autoComplete="off"
                />
              </Grid>
              <Grid size={6}></Grid>
              <Grid size={12}>
                <TextField
                  name="specialAccommodation"
                  value={specialAccommodation}
                  onChange={handleChange}
                  fullWidth
                  label={t(`${NSPP_TRAVEL}:preferences.specialAccommodation`)}
                  multiline
                  maxRows={4}
                  variant="standard"
                  slotProps={{
                    htmlInput: { maxLength: 200 }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid size={7}></Grid>
      </Grid>
    </Box>
  );

  function handleChange({ target }) {
    setState(function(state) {
      try {
        const nextState = { ...state, [target.name]: target.value };
        validationSchema.validateSyncAt(target.name, nextState);
        return nextState;
      } catch (e) {
        return state;
      }
    });
  }

  function onSave() {
    setSaving(true);
    PatientTravelApi.saveHotelPreferences(patientId, state)
      .then(
        function() {
          setTravelPreferences(function(prevState) {
            return { ...prevState, hotelPreferences: state };
          });
          NotificationManager.success('Hotel Preferences saved successfully');
        },
        function(error) {
          NotificationManager.error('Hotel Preferences save error');
        }
      )
      .finally(function() {
        setSaving(false);
      });
  }

  function onDiscard() {
    setState(initialData);
  }
}
